// ./src/components/layouts/DashboardLayout/PWAInstallBanner.tsx

import React from "react";
import { Paper, Group, Box, Text, Button, CloseButton, useMantineTheme } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { usePWA, useInstallPrompt } from "@/contexts/PWAContext";

import { AppIcon } from "@/components/generic/AppIcon";

export const PWAInstallBanner: React.FC = () => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { available, prompt, dismiss, platform } = useInstallPrompt();
  const { isPWA } = usePWA();

  // Don't show if already installed
  if (isPWA) {
    return null;
  }

  // iOS-specific instructions (no native install prompt)
  if (platform === "ios") {
    return (
      <Paper
        p="sm"
        radius={0}
        bg="neutral.0"
        style={{
          borderBottom: `1px solid ${theme.colors.neutral[2]}`,
        }}
      >
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="sm" wrap="nowrap">
            <Box
              w={32}
              h={32}
              bg="neutral.1"
              style={{
                borderRadius: theme.radius.md,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <AppIcon iconName="DeviceMobile" size={18} color={theme.colors.neutral[6]} />
            </Box>
            <Box>
              <Text size="sm" fw={600} c="neutral.8">
                {t("pwa.installApp", "Install CarExpenses")}
              </Text>
              <Text size="xs" c="neutral.5">
                {t("pwa.iosInstructions", "Tap the share button and 'Add to Home Screen'")}
              </Text>
            </Box>
          </Group>
          <CloseButton size="sm" onClick={dismiss} />
        </Group>
      </Paper>
    );
  }

  // Don't show if prompt not available
  if (!available) {
    return null;
  }

  return (
    <Paper
      p="sm"
      radius={0}
      bg="neutral.0"
      style={{
        borderBottom: `1px solid ${theme.colors.neutral[2]}`,
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="sm" wrap="nowrap">
          <Box
            w={32}
            h={32}
            bg="brand.0"
            style={{
              borderRadius: theme.radius.md,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AppIcon iconName="DeviceMobile" size={18} color={theme.colors.brand[6]} />
          </Box>
          <Box>
            <Text size="sm" fw={600} c="neutral.8">
              {t("pwa.installApp", "Install CarExpenses")}
            </Text>
            <Text size="xs" c="neutral.5">
              {t("pwa.installDescription", "Add to your home screen for quick access")}
            </Text>
          </Box>
        </Group>
        <Group gap="xs" wrap="nowrap">
          <Button size="xs" variant="filled" color="brand" onClick={prompt}>
            {t("pwa.install", "Install")}
          </Button>
          <CloseButton size="sm" onClick={dismiss} />
        </Group>
      </Group>
    </Paper>
  );
};